import { PiletDataApi } from './piralTypes';
import {
	createPluginUserSettingsApi,
	PluginUserSettingsApi,
} from './PluginUserSettingsApi';
import { SettingValue, UpdateSettingValueRequest } from './types';

const settingsPiletDataKeyPrefix = 'sfplugin-usersettings-sdk-';

export interface PiletUserSettingsApi {
	/**
	 * Initialize the plugin settings.
	 * @param callback A callback to be called when the settings are initialized.
	 */
	initializePluginSettings(
		callback?: (success: boolean, settings?: SettingValue[]) => void
	): Promise<void>;
	/**
	 * Get all setting values for the plugin.
	 * @returns All setting values for the plugin.
	 */
	getAllSettingValues(): Promise<SettingValue[]>;
	/**
	 * Set setting values for the plugin.
	 * @param requests The setting values to set.
	 */
	setSettingValues(requests: UpdateSettingValueRequest[]): Promise<void>;
	/**
	 * Get the value of a setting.
	 * @param key The key of the setting.
	 * @param defaultValue The default value to return if the setting is not found.
	 * @returns The value of the setting or the default value if the setting is not found.
	 * @template T The type of the setting value.
	 * @example
	 * ```ts
	 * const value = client.getSettingValue('isFeatureEnabled', false);
	 * ```
	 */
	getSettingValue<T>(key: string, defaultValue: T | undefined): T | undefined;
}

class PiletUserSettingsApiImpl implements PiletUserSettingsApi {
	private initialized = false;
	private initPromise: Promise<SettingValue[]> | undefined;
	private api: PluginUserSettingsApi;

	public constructor(
		private piletApi: PiletDataApi,
		private pluginName: string,
		pluginBaseUrl: string,
		apiVersion = 'v1'
	) {
		this.api = createPluginUserSettingsApi(pluginBaseUrl, apiVersion);
	}

	public async initializePluginSettings(
		callback?: (success: boolean, settings?: SettingValue[]) => void
	): Promise<void> {
		if (this.initialized) {
			if (callback) {
				callback(true, this.getStoredSettings());
			}
			return;
		} else if (this.initPromise) {
			// Initialization is already in progress
			return;
		} else {
			const values = this.getStoredSettings();
			if (values.length > 0) {
				this.initialized = true;
				if (callback) {
					callback(true, values);
				}
				return;
			}
		}

		let success = false;
		let settings: SettingValue[] | undefined;

		try {
			this.initPromise = this.api.getAllSettingValues(true);
			settings = await this.initPromise;

			// Bad, bad, bad. Shouldn't use setData. It will be fixed once AppShell supports plugin settings
			success = this.piletApi.setData(
				settingsPiletDataKeyPrefix + this.pluginName,
				settings
			);
			this.initialized = success;
		} catch (error) {
			success = false;
		}

		if (callback) {
			callback(success, settings);
		}
	}

	public async getAllSettingValues(): Promise<SettingValue[]> {
		let values: SettingValue[] | undefined;

		if (this.initialized) {
			values = this.getStoredSettings();
		} else if (this.initPromise) {
			values = await this.initPromise;
		}

		return values || [];
	}

	public async setSettingValues(requests: UpdateSettingValueRequest[]): Promise<void> {
		if (!this.initialized && this.initPromise) {
			await this.initPromise;
		}

		await this.api.setSettingValues(requests);

		// Merge the updated settings with the existing settings stored in the pilet data
		const existingSettings = this.getStoredSettings();
		const updatedSettings = existingSettings.map(es => {
			const updatedSetting = requests.find(rs => rs.key === es.key);
			return updatedSetting ? { ...es, value: updatedSetting.value } : es;
		});
		this.piletApi.setData(settingsPiletDataKeyPrefix + this.pluginName, updatedSettings);
	}

	public getSettingValue<T>(key: string, defaultValue: T | undefined): T | undefined {
		const settings = this.getStoredSettings();
		const setting = settings?.find(s => s.key === key);

		if (setting && setting.value !== undefined) {
			return setting.value as T;
		}

		return defaultValue;
	}

	private getStoredSettings(): SettingValue[] {
		return (
			(this.piletApi.getData(
				settingsPiletDataKeyPrefix + this.pluginName
			) as SettingValue[]) || []
		);
	}
}

export function createPiletUserSettingsApi(
	piletApi: PiletDataApi,
	pluginName: string,
	pluginBaseUrl: string,
	apiVersion = 'v1'
): PiletUserSettingsApi {
	return new PiletUserSettingsApiImpl(piletApi, pluginName, pluginBaseUrl, apiVersion);
}
